import { api } from "./api";

/**
 * SAAS-2 · 订阅版（个人站）顶栏工具区 / 应用中心 / 更新说明 / 每人偏好 数据层（后端 routes/subscription.py）。
 * 🔴 /subscription/* 与 /release-notes* 在企业版一律 404——前端只在 useEdition() === "subscription" 时才调用。
 * 契约样稿：designs/mockups/theme/个人站订阅版-样稿.html ① ② ③（tag saas-mockup-v1）；数据点 × 接口见 src/chrome/API_MAP_saas2.md。
 */
export interface SubscriptionModule {
  key: string;
  group: "free" | "paid";       // SAAS-2b · 系统功能 · 免费 / 增值服务 · 付费
  name: string;
  desc: string;
  icon: string;                 // lucide-react 图标名
  route: string | null;         // null = 没有页面（不出「进入」）
  /** 后端 module_status：已开通 / 试用中 / 可开通 / 即将上线（前端不自己判定） */
  status: "enabled" | "trial" | "available" | "coming_soon";
  trial_until: string | null;   // 付费模块试用到期日 YYYY-MM-DD（含当天）
}
export interface SubscriptionModules {
  plan_name: string | null;     // null = 没设（显示「—」）
  contact: { phone: string | null; email: string | null };
  modules: SubscriptionModule[];
}
export interface SubscriptionToolbar {
  help_center_url: string | null;   // null = 帮助入口整个不显示
  mobile_site_url: string | null;   // 系统设置 → 通用设置「手机站地址」（SAAS-2b）；null = 不出「复制地址 / 二维码」
}
export interface ReleaseNote {
  id: string;
  title: string;
  body: string;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  is_read?: boolean | null;
}
export interface ReleaseNotesPage {
  items: ReleaseNote[];
  total: number;
  unread_count: number;
}
export interface ReleaseNoteWrite {
  title: string;
  body: string;
  is_published: boolean;
}
export interface MyPreferences {
  new_order_alert: boolean;
}

export const SUB_QK = {
  modules: ["subscription", "modules"] as const,
  toolbar: ["subscription", "toolbar"] as const,
  notes: ["subscription", "release-notes"] as const,
  manage: ["subscription", "release-notes", "manage"] as const,
  prefs: ["me", "preferences"] as const,
};

export const subscriptionApi = {
  modules: (): Promise<SubscriptionModules> => api("/api/v1/subscription/modules"),
  toolbar: (): Promise<SubscriptionToolbar> => api("/api/v1/subscription/toolbar"),
  notes: (limit = 3, offset = 0): Promise<ReleaseNotesPage> => api(`/api/v1/release-notes?limit=${limit}&offset=${offset}`),
  readNote: (id: string): Promise<{ unread_count: number }> => api(`/api/v1/release-notes/${id}/read`, { method: "POST" }),
  readAll: (): Promise<{ unread_count: number }> => api("/api/v1/release-notes/read-all", { method: "POST" }),
  manageNotes: (): Promise<ReleaseNote[]> => api("/api/v1/release-notes/manage"),
  createNote: (body: ReleaseNoteWrite): Promise<ReleaseNote> => api("/api/v1/release-notes", { method: "POST", body: JSON.stringify(body) }),
  updateNote: (id: string, body: Partial<ReleaseNoteWrite>): Promise<ReleaseNote> =>
    api(`/api/v1/release-notes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteNote: (id: string): Promise<{ ok: boolean }> => api(`/api/v1/release-notes/${id}`, { method: "DELETE" }),
  prefs: (): Promise<MyPreferences> => api("/api/v1/me/preferences"),
  setPrefs: (body: Partial<MyPreferences>): Promise<MyPreferences> => api("/api/v1/me/preferences", { method: "PATCH", body: JSON.stringify(body) }),
};

/** 新订单提醒：RealtimeBridge 收到 /ws 的 order.created 后在 window 上派发这个事件（载荷 = 后端 data：{count, orders[{order_id, order_no, source}]}）。 */
export const ORDER_CREATED_EVENT = "ota:order-created";
export interface OrderCreatedDetail {
  count: number;
  orders: { order_id: string | null; order_no: string; source: string }[];
}
